import { t } from '@lingui/macro';
import React, { useState, useCallback, useMemo, useEffect, useContext } from 'react';

import {
  getAttachmentFaces,
  getIdentities,
  type AttachmentFaceResponse,
  type IdentityResponse
} from '@api/v4/private/people_tagging/faces';
import useDebounce from '@components/common/custom_hooks/useDebounce';
import { I18nProviderWrapper } from '@components/common/I18nProviderWrapper';
import { SuggestedNamesList } from '@components/people_tags/common/SuggestedNamesList';
import { TextSanitizationTooltip } from '@components/shared/sanitization';

import assetModalContext from '../asset_modal_context';
import { ActionTypes, type PeopleTaggingChange } from '../EditTabTypes';

import { FaceThumbnail } from './FaceThumbnail';
import { SameNameDetectedModal } from './SameNameDetectedModal';

import Wrapper, {
  PeopleSection,
  SectionHeader,
  HeaderText,
  PeopleContent,
  PeopleTagsContainer,
  PersonTag,
  PersonImage,
  PersonName,
  PersonNameText,
  RemovePersonButton,
  CustomDropdown,
  DropdownHeader,
  SelectedOption,
  OptionImage,
  OptionText,
  DropdownIcon,
  InputStyled
} from './styles/PeopleInAssetsComponents';

// Face data structure used internally
interface FaceData {
  identityId?: string; // API face id
  faceId?: string; // API face id
  name: string; // identity_name from API (empty string if not named)
  assetUrl: string; // source_url from API
  boundingBox: {
    width: number;
    height: number;
    left: number;
    top: number;
  };
}

interface PeopleInAssetProps {
  // Props removed - will get from context and BFG
}

function PeopleInAsset({}: PeopleInAssetProps): React.ReactElement {
  const { state, dispatch } = useContext(assetModalContext);

  // Get attachment key and brandfolder key
  const attachmentKey = state?.editState?.firstAttachmentKey || '';
  const brandfolderKey = BFG?.resource?.key || BFG?.brandfolder_key || '';

  // State to manage faces data
  const [faces, setFaces] = useState<FaceData[]>([]);
  const [identities, setIdentities] = useState<IdentityResponse[]>([]);
  const [isLoadingFaces, setIsLoadingFaces] = useState<boolean>(true);

  // State to track selected person for each face (only for unnamed faces)
  const [selectedPeople, setSelectedPeople] = useState<
  Record<string, IdentityResponse | null>
  >({});

  // State for dropdown open/close status (keyed by faceId)
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});

  // State for search input for each unnamed face (keyed by faceId)
  const [searchInputs, setSearchInputs] = useState<Record<string, string>>({});

  // Use debounce hook for search inputs
  const debouncedSearchInputs = useDebounce({
    delay: 300,
    value: searchInputs
  });

  // State for same name modal
  const [showSameNameModal, setShowSameNameModal] = useState(false);
  const [pendingPersonName, setPendingPersonName] = useState('');
  const [pendingFaceId, setPendingFaceId] = useState<string | null>(null);

  // Fetch faces on mount
  useEffect(() => {
    if (!attachmentKey || !brandfolderKey) return;

    const loadFaces = async (): Promise<void> => {
      try {
        setIsLoadingFaces(true);
        const [facesResponse, identitiesResponse] = await Promise.all([
          getAttachmentFaces({ attachmentKey, brandfolderKey }),
          getIdentities({ brandfolderKey })
        ]);

        // Transform API response to FaceData
        const transformedFaces: FaceData[] = facesResponse.map((face: AttachmentFaceResponse) => ({
          identityId: face.identity_id,
          faceId: face.id,
          name: face.identity_name || '',
          assetUrl: face.source_url,
          boundingBox: {
            width: face.bounding_box.width,
            height: face.bounding_box.height,
            left: face.bounding_box.left,
            top: face.bounding_box.top
          }
        }));

        setFaces(transformedFaces);
        setIdentities(identitiesResponse);
      } catch (error) {
        console.error(t`Error loading faces:`, error);
        setFaces([]);
      } finally {
        setIsLoadingFaces(false);
      }
    };

    void loadFaces();
  }, [attachmentKey, brandfolderKey]);

  // Toggle dropdown open/close
  const toggleDropdown = useCallback((faceId: string): void => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [faceId]: !prev[faceId]
    }));
  }, []);

  // Close dropdown
  const closeDropdown = useCallback((faceId: string): void => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [faceId]: false
    }));
  }, []);

  // Handle search input change
  const handleSearchInputChange = useCallback((faceId: string, value: string): void => {
    setSearchInputs((prev) => ({
      ...prev,
      [faceId]: value
    }));

    // Open dropdown when user starts typing
    if (value.trim() !== '') {
      setOpenDropdowns((prev) => ({
        ...prev,
        [faceId]: true
      }));
    } else {
      // Close dropdown when input is empty
      closeDropdown(faceId);
    }
  }, [closeDropdown]);

  // Memoize filtered identities for each face based on search input
  const memoizedFilteredIdentities = useMemo(() => {
    const result: Record<string, IdentityResponse[]> = {};

    // Pre-compute filtered identities for each unnamed face
    faces.forEach((face) => {
      if (face.name) return; // Skip named faces

      const searchTerm = debouncedSearchInputs[face.identityId]?.toLowerCase() || '';

      if (!searchTerm) {
        result[face.identityId] = identities;
      } else {
        result[face.identityId] = identities.filter((identity) =>
          identity.name.toLowerCase().includes(searchTerm)
        );
      }
    });

    return result;
  }, [debouncedSearchInputs, faces, identities]);

  // Memoize the mapping function to convert identities to suggestions format
  const mapIdentitiesToSuggestions = useCallback((identityList: IdentityResponse[]) => {
    return identityList.map((identity) => ({
      face_uri: identity.face_uri || '',
      person_id: identity.id,
      tag_name: identity.name,
      tag_count: identity.tag_count
    }));
  }, []);

  // Memoize suggestions for each face
  const memoizedSuggestions = useMemo(() => {
    type Suggestion = { face_uri: string; person_id: string; tag_name: string; tag_count?: number };
    const result: Record<string, Suggestion[]> = {};

    // Pre-compute suggestions for each unnamed face
    faces.forEach((face) => {
      if (face.name) return; // Skip named faces

      const filteredIdentities = memoizedFilteredIdentities[face.identityId] || identities;
      result[face.identityId] = mapIdentitiesToSuggestions(filteredIdentities);
    });

    return result;
  }, [memoizedFilteredIdentities, faces, identities, mapIdentitiesToSuggestions]);

  // Get suggestions for a specific face
  const getSuggestionsForFace = useCallback((faceId: string) => {
    return memoizedSuggestions[faceId] || mapIdentitiesToSuggestions(identities);
  }, [memoizedSuggestions, identities, mapIdentitiesToSuggestions]);

  // Handle removing a face (deleting it)
  const handleRemovePerson = useCallback((identityId: string, faceId?: string): void => {
    if (!dispatch) return;

    // Find the face to get its current name
    const face = faces.find((f) => f.identityId === identityId);
    if (!face) return;

    // Update local UI state optimistically
    setFaces((prev) => prev.filter((f) => f.identityId !== identityId));

    // Add to pending changes for save
    const currentChanges = state?.editState?.peopleTaggingChanges || [];
    const newChange: PeopleTaggingChange = {
      identityId,
      faceId: faceId || '',
      name: face.name || '',
      type: 'delete',
      attachmentKey
    };

    // Remove any existing change for this faceId and add new one
    const filteredChanges = currentChanges.filter((change) => change.identityId !== identityId);
    dispatch({
      type: ActionTypes.UpdateAsset,
      payload: { peopleTaggingChanges: [...filteredChanges, newChange] }
    });
  }, [dispatch, faces, state?.editState?.peopleTaggingChanges, attachmentKey]);

  // Handle identifying a person from dropdown or creating new one
  const handleIdentifyPerson = useCallback((identityId: string, name: string): void => {
    if (!dispatch) return;

    const trimmedName = name.trim();

    // Update local UI state optimistically
    setFaces((prev) =>
      prev.map((face) =>
        face.identityId === identityId ? { ...face, name: trimmedName } : face
      )
    );

    // Clean up selected people state
    setSelectedPeople((prev) => {
      const newState = { ...prev };
      delete newState[identityId];
      return newState;
    });

    // Clear search input
    setSearchInputs((prev) => {
      const newState = { ...prev };
      delete newState[identityId];
      return newState;
    });

    // Close dropdown
    closeDropdown(identityId);

    // Add to pending changes for save
    const currentChanges = state?.editState?.peopleTaggingChanges || [];
    const newChange: PeopleTaggingChange = {
      identityId: identityId,
      name: trimmedName,
      type: 'update'
    };

    // Remove any existing change for this faceId and add new one
    const filteredChanges = currentChanges.filter((change) => change.identityId !== identityId);
    dispatch({
      type: ActionTypes.UpdateAsset,
      payload: { peopleTaggingChanges: [...filteredChanges, newChange] }
    });
  }, [dispatch, state?.editState?.peopleTaggingChanges, closeDropdown]);

  // Handle creating a new person with the entered name
  const handleCreateNewPerson = useCallback((identityId: string, name: string): void => {
    if (!name.trim()) return;

    // Check if there's an existing identity with the same name
    const existingIdentity = identities.find((identity) =>
      identity.name.toLowerCase() === name.toLowerCase()
    );

    if (existingIdentity) {
      // Show same name modal
      setPendingPersonName(name.trim());
      setPendingFaceId(identityId);
      setShowSameNameModal(true);
    } else {
      // Create a new person with the entered name (use empty string as identityId for new person)
      handleIdentifyPerson(identityId, name.trim());
    }
  }, [identities, handleIdentifyPerson]);

  // Handle canceling the same name modal - just close it without taking any action
  const handleUseExistingPerson = useCallback((): void => {
    // Just close the modal and reset state
    setShowSameNameModal(false);
    setPendingPersonName('');
    setPendingFaceId(null);
  }, []);

  // Handle creating a new person with the same name
  const handleCreateNewPersonWithSameName = useCallback((): void => {
    if (pendingFaceId && pendingPersonName) {
      // Create a new person with the same name
      handleIdentifyPerson(pendingFaceId, pendingPersonName);
    }

    // Close modal
    setShowSameNameModal(false);
    setPendingPersonName('');
    setPendingFaceId(null);
  }, [pendingFaceId, pendingPersonName, handleIdentifyPerson]);

  if (isLoadingFaces) {
    return (
      <I18nProviderWrapper>
        <Wrapper>
          <PeopleSection>
            <SectionHeader>
              <HeaderText>{t`People in asset`}</HeaderText>
            </SectionHeader>
            <PeopleContent>
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <span className="icon bff-spinner" />
              </div>
            </PeopleContent>
          </PeopleSection>
        </Wrapper>
      </I18nProviderWrapper>
    );
  }

  return (
    <I18nProviderWrapper>
      <Wrapper>
        <PeopleSection>
          <SectionHeader>
            <HeaderText>{t`People in asset`}</HeaderText>
            <TextSanitizationTooltip
              margin
              tooltipText={t`
                Name the faces in this asset by selecting from existing people or typing a new name.
                If a face was misidentified, remove the name and enter the correct one —
                this change only affects the current asset.
              `}
            />
          </SectionHeader>
          <PeopleContent>
            <PeopleTagsContainer>
              {faces.map((face) => {
                const hasName = face.name && face.name.trim() !== '';

                if (hasName) {
                  // Render named face with name and remove button
                  return (
                    <PersonTag key={face.identityId}>
                      <PersonImage>
                        <FaceThumbnail
                          alt={face.name}
                          assetUrl={face.assetUrl}
                          // assetUrl="https://upload.wikimedia.org/wikipedia/commons/8/8d/President_Barack_Obama.jpg"
                          boundingBox={face.boundingBox}
                          size={48}
                        />
                      </PersonImage>
                      <PersonName>
                        <PersonNameText>{face.name}</PersonNameText>
                        <RemovePersonButton
                          aria-label={t`Remove ${face.name}`}
                          onClick={(): void => handleRemovePerson(face.identityId, face.faceId)}
                        >
                          <span className="icon bff-close" />
                        </RemovePersonButton>
                      </PersonName>
                    </PersonTag>
                  );
                } else {
                  // Render unnamed face with input and dropdown
                  const selectedPerson = selectedPeople[face.identityId];
                  const isOpen = openDropdowns[face.identityId] || false;

                  return (
                    <PersonTag key={face.identityId}>
                      <PersonImage>
                        <FaceThumbnail
                          alt={t`Unidentified person`}
                          assetUrl={face.assetUrl}
                          // assetUrl="https://upload.wikimedia.org/wikipedia/commons/8/8d/President_Barack_Obama.jpg"
                          boundingBox={face.boundingBox}
                          size={48}
                        />
                      </PersonImage>
                      <PersonName>
                        <CustomDropdown>
                          <DropdownHeader>
                            {selectedPerson ? (
                              <SelectedOption>
                                <OptionImage
                                  alt={selectedPerson.name}
                                  src={selectedPerson.face_uri || ''}
                                />
                                <OptionText>{selectedPerson.name}</OptionText>
                              </SelectedOption>
                            ) : (
                              <InputStyled
                                onChange={(e): void => handleSearchInputChange(face.identityId, e.target.value)}
                                placeholder={t`Name person`}
                                type="text"
                                value={searchInputs[face.identityId] || ''}
                              />
                            )}
                            <DropdownIcon onClick={(): void => toggleDropdown(face.identityId)}>
                              <span className="icon bff-caret-down" />
                            </DropdownIcon>
                          </DropdownHeader>

                          {isOpen && (
                            <SuggestedNamesList
                              handleCreateNewPerson={(name): void => {
                                handleCreateNewPerson(face.identityId, name);
                              }}
                              handleOnSelectTargetPerson={(selected): void => {
                                handleIdentifyPerson(face.identityId, selected.tag_name);
                              }}
                              showCreateOption={!!searchInputs[face.identityId]?.trim()}
                              suggestions={getSuggestionsForFace(face.identityId)}
                              typedName={searchInputs[face.identityId] || ''}
                            />
                          )}
                        </CustomDropdown>
                      </PersonName>
                    </PersonTag>
                  );
                }
              })}
            </PeopleTagsContainer>
          </PeopleContent>
        </PeopleSection>
      </Wrapper>
      {/* Same Name Detected Modal */}
      <SameNameDetectedModal
        isOpen={showSameNameModal}
        onCancel={handleUseExistingPerson}
        onCreateNew={(): void => {
          void handleCreateNewPersonWithSameName();
        }}
      />
    </I18nProviderWrapper>
  );
}

export default PeopleInAsset;
